import { SQUAD_DEFS } from '../../data/squads.js';
import { getMap } from '../../data/maps/index.js';
import type { Vec2 } from '../../utils/vec2.js';
import type { MatchState } from '../State.js';
import { SECONDS_PER_TICK, makeSquad } from '../State.js';
import type { ProductionSlot } from '../entities/Core.js';
import type { SquadArchetype } from '../entities/Squad.js';
import type { EntityId, PlayerId } from '../ids.js';
import { entityId } from '../ids.js';

const SPAWN_OFFSET_PX = 80;
const SPAWN_SPACING_PX = 56;
const SPAWN_BLOCKED_RADIUS_PX = 48;

// Validation of ProduceSquadCommand (signal cost, squadCap, ownership) lives in
// Simulation.applyCommand, so the queue here is already authoritative.
//
// Producers: the Core (always — Core.produces is non-null for a live Core)
// and each operational Production Hub. Hubs ADD a parallel queue rather than
// replacing the Core; with the 2-Hub cap a player can have up to three
// independent queues running. Spawn rule: when progress crosses 1.0, mint a
// squad next to the producer, pop the queue head, reset progress, and
// increment owner squadCount.
export function productionSystem(state: MatchState): void {
  for (const core of Object.values(state.cores)) {
    if (core.produces === null) continue;
    advanceSlot(state, core.produces, core.pos, core.ownerId);
  }
  for (const b of Object.values(state.buildings)) {
    if (b.kind !== 'production_hub') continue;
    if (b.buildProgress < 1) continue;
    advanceSlot(state, b.produces, b.pos, b.ownerId);
  }
}

function advanceSlot(
  state: MatchState,
  slot: ProductionSlot,
  producerPos: Vec2,
  ownerId: PlayerId,
): void {
  const head = slot.queue[0];
  if (head === undefined) {
    slot.progress = 0;
    return;
  }
  const def = SQUAD_DEFS[head];
  slot.progress += SECONDS_PER_TICK / def.buildTimeSec;
  if (slot.progress < 1.0) return;
  spawnSquadAt(state, producerPos, ownerId, head);
  slot.queue.shift();
  slot.progress = 0;
}

function spawnSquadAt(state: MatchState, producerPos: Vec2, ownerId: PlayerId, archetype: SquadArchetype): void {
  const owner = state.players[ownerId];
  // Spawn toward the map's vertical mid so squads aren't pinned against the map edge.
  // Foundry places Core 0 near y=220 and Core 1 near y=1780 (mid is y=1000).
  const map = getMap(state.mapId);
  const mapMidY = map.height / 2;
  const dirSign = producerPos.y < mapMidY ? 1 : -1;
  const pos = chooseSpawnPos(state, producerPos, dirSign);
  const facing = dirSign === 1 ? Math.PI / 2 : -Math.PI / 2;
  const id: EntityId = mintEntityId(state);
  state.squads[id] = makeSquad({ id, ownerId, archetype, pos, facing });
  owner.squadCount += 1;
  // Lifetime counter for the end-of-match summary. Read by the EOM panel; the UI
  // never has to recompute this from past spawns (Rule 1).
  state.stats.squadsBuilt[ownerId] += 1;
}

function chooseSpawnPos(state: MatchState, producerPos: Vec2, dirSign: 1 | -1): Vec2 {
  const map = getMap(state.mapId);
  const forward = { x: 0, y: dirSign };
  const right = { x: 1, y: 0 };
  const anchor = {
    x: producerPos.x + forward.x * SPAWN_OFFSET_PX,
    y: producerPos.y + forward.y * SPAWN_OFFSET_PX,
  };

  for (const offset of spawnOffsets()) {
    const pos = clampToMap(map, {
      x: anchor.x + right.x * offset.lateral + forward.x * offset.depth,
      y: anchor.y + right.y * offset.lateral + forward.y * offset.depth,
    });
    if (isBlockedByObstacle(map, pos)) continue;
    if (isTooCloseToExistingSquad(state, pos)) continue;
    return pos;
  }

  return clampToMap(map, anchor);
}

function spawnOffsets(): Array<{ lateral: number; depth: number }> {
  const offsets: Array<{ lateral: number; depth: number }> = [{ lateral: 0, depth: 0 }];
  for (let row = 0; row < 4; row++) {
    const depth = row * SPAWN_SPACING_PX;
    for (let col = 1; col <= 3; col++) {
      const lateral = col * SPAWN_SPACING_PX;
      offsets.push({ lateral, depth });
      offsets.push({ lateral: -lateral, depth });
    }
    if (row > 0) offsets.push({ lateral: 0, depth });
  }
  return offsets;
}

function isTooCloseToExistingSquad(state: MatchState, pos: Vec2): boolean {
  const minDistSq = SPAWN_BLOCKED_RADIUS_PX * SPAWN_BLOCKED_RADIUS_PX;
  for (const squad of Object.values(state.squads)) {
    const dx = squad.pos.x - pos.x;
    const dy = squad.pos.y - pos.y;
    if (dx * dx + dy * dy < minDistSq) return true;
  }
  return false;
}

function isBlockedByObstacle(map: ReturnType<typeof getMap>, pos: Vec2): boolean {
  for (const o of map.obstacles) {
    if (pos.x >= o.x && pos.x <= o.x + o.w && pos.y >= o.y && pos.y <= o.y + o.h) return true;
  }
  return false;
}

function clampToMap(map: ReturnType<typeof getMap>, pos: Vec2): Vec2 {
  return {
    x: clamp(pos.x, 1, map.width - 1),
    y: clamp(pos.y, 1, map.height - 1),
  };
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function mintEntityId(state: MatchState): EntityId {
  const id = state.nextEntityId;
  state.nextEntityId = id + 1;
  return entityId(id);
}
